import { useTranslation } from 'react-i18next';
import { Typography, Empty, Button, List, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetCountries } from 'api/countries/use-get-countries';
import { getCargoTypeLabel } from 'shared/utils/cargos/cargo-type-labels.ts';
import { findCountriesById } from 'shared/utils/countries';
import type { ICargoFormWithId } from 'components/cargos/cargo-create-with-order/types';
import styles from './cargo-list.module.scss';
import type { ICargoListProps } from './types';

const { Title, Text } = Typography;

export const CargoList = (props: ICargoListProps) => {
  const { cargos, handleOpenCargoForm, onDeleteCargo, onEditCargo } = props;
  const { t } = useTranslation();
  const { data: countries } = useGetCountries();

  const formatCargoDisplayText = (cargo: ICargoFormWithId) => {
    const loadingCountry = findCountriesById(cargo.loadingAddress.countryId, countries);
    const unloadingCountry = findCountriesById(cargo.unloadingAddress.countryId, countries);

    const loadingLocation = `${loadingCountry?.code}, ${cargo.loadingAddress.city}`;
    const unloadingLocation = `${unloadingCountry?.code}, ${cargo.unloadingAddress.city}`;

    return `${loadingLocation} → ${unloadingLocation}`;
  };

  if (cargos.length === 0) {
    return (
      <div className={styles.cargoListContainer}>
        <Title level={5}>{t('cargo:cargos')}</Title>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('cargo:no_cargos_added')}>
          <Button type='primary' icon={<PlusOutlined />} onClick={handleOpenCargoForm}>
            {t('cargo:add_cargo')}
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.cargoListContainer}>
      <div className={styles.header}>
        <Title level={5} className={styles.title}>
          {t('cargo:cargos')} ({cargos.length})
        </Title>
        <Button type='dashed' icon={<PlusOutlined />} size='small' onClick={handleOpenCargoForm}>
          {t('cargo:add_cargo')}
        </Button>
      </div>

      <List
        size='small'
        dataSource={cargos}
        renderItem={(cargo, index) => (
          <List.Item
            key={cargo.id}
            actions={[
              <Button
                key={`edit-${cargo.id}`}
                icon={<EditOutlined />}
                size='small'
                onClick={() => onEditCargo(cargo.id)}
              />,
              <Button
                key={`delete-${cargo.id}`}
                icon={<DeleteOutlined />}
                size='small'
                danger
                onClick={() => onDeleteCargo(cargo.id)}
              />,
            ]}>
            <div>
              <Space>
                {!cargo.isCompleted && <WarningOutlined className={styles.warningIcon} />}
                <Text strong>
                  {t('cargo:cargo')} #{index + 1}
                </Text>
                <Text>({getCargoTypeLabel(cargo.type)})</Text>
              </Space>
              <br />
              <Text type='secondary' className={styles.secondaryText}>
                {formatCargoDisplayText(cargo)}
              </Text>
            </div>
          </List.Item>
        )}
        bordered
      />
    </div>
  );
};
